'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocale } from 'next-intl'

import { useGetFlightOffersQuery } from '@/src/api/rtkQuery/offersApi'
import { getCurrentLocale } from '@/src/utils/rtkQuery/getCurrentLocale'
import { domainCountry } from '@/src/constants/domain'

import HomepageCarouselComponent from './homepageCarousel'

// Constants
const AUTO_PLAY_INTERVAL = 3000 // 3 seconds
const SCROLL_ANIMATION_DURATION = 1000 // 1 second for smooth scroll

export const HomepageCarousel: React.FC = () => {
    const locale = getCurrentLocale()
    const currentLocale = useLocale()
    const isRTL = currentLocale === 'ar'

    // Fetch offers data
    const { data: offersData, error } = useGetFlightOffersQuery({
        domain: domainCountry,
        homepageBanner: true
    })

    // State management
    const [currentIndex, setCurrentIndex] = useState(0)
    const [mobileIndex, setMobileIndex] = useState(0)
    const [isAutoPlaying, setIsAutoPlaying] = useState(true)

    // Refs
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const isScrollingProgrammatically = useRef(false)

    // Get 3 consecutive cards for desktop carousel
    const getVisibleCards = useCallback(() => {
        if (!offersData?.offers?.length) return []

        const visibleCards = []
        for (let i = 0; i < 3; i++) {
            const index = (currentIndex + i) % offersData.offers.length
            visibleCards.push(offersData.offers[index])
        }
        return visibleCards
    }, [currentIndex, offersData?.offers])

    // Scroll to specific card on mobile
    const scrollToCard = useCallback(
        (index: number, isProgrammatic = false) => {
            if (!scrollContainerRef.current) return

            isScrollingProgrammatically.current = isProgrammatic
            const container = scrollContainerRef.current
            const cardWidth = container.offsetWidth

            const scrollPosition = cardWidth * index

            container.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
            })

            // Reset flag after animation completes (smooth scroll takes ~800-1000ms)
            setTimeout(() => {
                isScrollingProgrammatically.current = false
            }, SCROLL_ANIMATION_DURATION)
        },
        [isRTL]
    )

    // Handle mobile scroll - update mobile index for manual swipes
    const handleMobileScroll = useCallback(() => {
        if (!scrollContainerRef.current || isScrollingProgrammatically.current) return

        const container = scrollContainerRef.current
        const { scrollLeft, offsetWidth } = container
        const offersLength = offersData?.offers?.length || 0

        // Find which card is currently in the center of the viewport
        let newIndex = 0
        let minDistance = Infinity

        // Check each card's position relative to viewport center
        for (let i = 0; i < offersLength; i++) {
            const card = container.children[i] as HTMLElement
            if (!card) continue

            const cardRect = card.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()
            const cardCenter = cardRect.left + cardRect.width / 2
            const viewportCenter = containerRect.left + containerRect.width / 2
            const distance = Math.abs(cardCenter - viewportCenter)

            if (distance < minDistance) {
                minDistance = distance
                newIndex = i
            }
        }

        // Validate index and check if it changed
        if (newIndex >= 0 && newIndex < offersLength && newIndex !== mobileIndex) {
            setMobileIndex(newIndex)

            // Update currentIndex only if different (for manual swipes)
            if (newIndex !== currentIndex) {
                setCurrentIndex(newIndex)
            }
        }
    }, [mobileIndex, currentIndex, offersData?.offers?.length])

    // Auto-play carousel logic
    useEffect(() => {
        if (!offersData?.offers?.length || !isAutoPlaying) return

        const timer = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % offersData.offers.length)
        }, AUTO_PLAY_INTERVAL)

        return () => clearInterval(timer)
    }, [offersData?.offers?.length, isAutoPlaying])

    // Sync mobile scroll position with currentIndex (for auto-play)
    useEffect(() => {
        if (currentIndex !== mobileIndex) {
            scrollToCard(currentIndex, true)
            setMobileIndex(currentIndex)
        }
        // Intentionally excluding scrollToCard from deps to prevent infinite loop
        // scrollToCard is stable due to useCallback with no dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, mobileIndex])

    // Navigation handlers
    const handlePrev = useCallback(() => {
        if (!offersData?.offers?.length) return
        setCurrentIndex(prevIndex => (prevIndex + 1) % offersData.offers.length)
    }, [offersData?.offers?.length])

    const handleNext = useCallback(() => {
        if (!offersData?.offers?.length) return
        setCurrentIndex(prevIndex => (prevIndex - 1 + offersData.offers.length) % offersData.offers.length)
    }, [offersData?.offers?.length])

    // Auto-play control handlers
    const handleMouseEnter = useCallback(() => {
        setIsAutoPlaying(false)
    }, [])

    const handleMouseLeave = useCallback(() => {
        setIsAutoPlaying(true)
    }, [])

    // Handle offer card click
    const handleOfferClick = useCallback(
        (slug: string | undefined) => {
            if (slug) {
                window.open(`/${locale}/offers/${slug}`, '_blank')
            }
        },
        [locale]
    )

    // Early return if no data or error
    if (error || !offersData?.offers?.length) {
        return null
    }

    const visibleCards = getVisibleCards()

    return (
        <HomepageCarouselComponent
            locale={locale}
            offers={offersData.offers}
            currentIndex={currentIndex}
            visibleCards={visibleCards}
            scrollContainerRef={scrollContainerRef}
            onPrev={handlePrev}
            onNext={handleNext}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onOfferClick={handleOfferClick}
            onMobileScroll={handleMobileScroll}
        />
    )
}

export default HomepageCarousel
